import { Switch, Tooltip } from 'antd'
import React from 'react'

import LoginToPractice from './LoginToPractice'
import {
  formatReminderNumWithoutCC,
  formatTimestamp,
  sortValues,
  formatProductName,
  formatFullProductName,
} from '../services/utils'

import css from './Dashboard.css'
import { getRecentPracticeIds } from '../services/practiceStorage'

// inline styles easiest way to override library css
const tableStyles = {
  th: {
    padding: '16px 8px',
    wordBreak: 'break-word',
  },
  td: {
    padding: '4px',
  },
}

const safeReadStats = (record, key, fallback) =>
  (record.stats && record.stats[key] ? record.stats[key] : fallback)

const getSecondLetter = (pmsType) => {
  let secondLetter = null
  if (pmsType === 'OrthoTrac' || pmsType === 'OrthoTracV2') {
    secondLetter = 'T'
  }
  if (pmsType === 'OpenDental') {
    secondLetter = 'D'
  }

  if (secondLetter) {
    return <span>{secondLetter}</span>
  }
}

// 'documentation' on the `columns` prop for antd Table
// https://ant.design/components/table/#Column
// https://github.com/react-component/table
const buildColumns = (
  loginToPractice,
  editPractice,
  isTopsAdmin,
  updateData,
  recents,
) => [
  {
    title: '',
    dataIndex: '_',
    render: (text, { practice }) => (
      <LoginToPractice
        loginToPractice={() => loginToPractice(practice)}
        editPractice={() => editPractice(practice.id)}
      />
    ),
  },
  {
    title: 'ID',
    dataIndex: 'id',
    defaultSortOrder: 'ascend',
    sorter: (recordA, recordB) => {
      let a = recordA.practice.id
      let b = recordB.practice.id

      if (recents) {
        const recentPracticeIds = getRecentPracticeIds().reverse()
        a = recentPracticeIds.indexOf(a)
        b = recentPracticeIds.indexOf(b)
      } else if (isTopsAdmin) {
        a = recordA.practice.external_id
        b = recordB.practice.external_id
      }

      return sortValues(parseInt(a, 10), parseInt(b, 10))
    },
    render: (text, { practice }) => {
      const { pms_type: pmsType } = practice
      return (
        <span
          className={`${css.cell} ${css.relativeCell} ${css.idCol}`}
          colSpan={1}
        >
          {!isTopsAdmin && pmsType && (
            <Tooltip
              key={`pms-${pmsType}`}
              title={pmsType}
              overlayStyle={{ maxWidth: '200px' }}
              placement="top"
            >
              <span className={css.pmsTile}>
                {pmsType[0]}
                {getSecondLetter(pmsType)}
              </span>
            </Tooltip>
          )}
          {isTopsAdmin ? practice.external_id : practice.id}
        </span>
      )
    },
  },
  {
    title: 'Practice Name',
    dataIndex: 'practiceName',
    defaultSortOrder: 'ascend',
    sorter: (recordA, recordB) => {
      const a = recordA.practice.name
      const b = recordB.practice.name

      return sortValues(a, b)
    },
    render: (text, { practice }) => {
      const { services_cancelled: servicesCancelled } = practice
      const cancelledClass = servicesCancelled && css.cancelled
      const pracClass =
        `${css.cell} ${css.left} ${css.practiceNameCol} ${cancelledClass}`

      return (
        servicesCancelled ? (
          <Tooltip
            key="cancelledPractice"
            title="This practice has cancelled their services."
            overlayStyle={{ maxWidth: '200px' }}
            placement="topLeft"
            arrowPointAtCenter
          >
            <span className={pracClass} colSpan={3}>
              {practice.name}
            </span>
          </Tooltip>
        ) : (
          <span className={pracClass} colSpan={3}>
            {practice.name}
          </span>
        )
      )
    },
  },
  {
    title: 'Active Locations',
    dataIndex: 'activeLocationsCount',
    onHeaderCell: () => ({ style: tableStyles.th }),
    onCell: () => ({ style: tableStyles.td }),
    render: (text, { activeLocationsCount }) => (
      <span className={`${css.cell} ${css.locationsCol}`} colSpan={2}>
        {activeLocationsCount}
      </span>
    ),
  },
  {
    title: 'Phone Numbers',
    dataIndex: 'phoneNumbers',
    onHeaderCell: () => ({ style: tableStyles.th }),
    onCell: () => ({ style: tableStyles.td }),
    render: (text, { phoneNumbers }) => (
      <span className={css.phoneCol} colSpan={3}>
        {phoneNumbers.map(number => (
          <div key={number}>
            <div dangerouslySetInnerHTML={{ __html: formatReminderNumWithoutCC(number) }} />
          </div>
            ))}
      </span>
    ),
  },
  {
    title: 'Products',
    dataIndex: 'products',
    onHeaderCell: () => ({ style: tableStyles.th }),
    render: (text, { products }) => (
      <span className={css.cell} colSpan={3}>
        {products.map(product => (
          <Tooltip
            key={`product-${product}`}
            title={formatFullProductName(product)}
            placement="top"
            arrowPointAtCenter
          >
            <div key={product} className={css.productTile}>
              {formatProductName(product)}
            </div>
          </Tooltip>
        ))}
      </span>
    ),
  },
  {
    title: 'Active',
    dataIndex: 'active',
    render: (text, { practice }) => (
      <span className={css.cell} colSpan={2}>
        <Switch
          onChange={() => updateData(practice.id, !practice.onboarding)}
          checked={!practice.onboarding}
        />
      </span>
    ),
  },
]

export default buildColumns



// WEBPACK FOOTER //
// ./src/Dashboard/TableColumns.js